'use client';

import React from 'react';

const serviceAreas = [
  { id: 'rio-rancho', label: 'Rio Rancho', isPrimary: true },
  { id: 'albuquerque', label: 'Albuquerque' },
  { id: 'bernalillo', label: 'Bernalillo' },
  { id: 'corrales', label: 'Corrales' },
  { id: 'placitas', label: 'Placitas' },
  { id: 'los-ranchos', label: 'Los Ranchos de Albuquerque' },
  { id: 'north-valley', label: 'North Valley' },
  { id: 'paradise-hills', label: 'Paradise Hills' },
  { id: 'taylor-ranch', label: 'Taylor Ranch' },
  { id: 'westside-abq', label: 'Westside Albuquerque' },
  { id: 'surrounding', label: 'And surrounding communities', isSurrounding: true },
];

const featureCards = [
  {
    id: 'response',
    emoji: '🚗',
    title: 'Fast Response Times',
    description:
      'We proudly serve Central New Mexico with quick scheduling and dependable service.',
  },
  {
    id: 'residential',
    emoji: '🏡',
    title: 'Residential & Commercial',
    description:
      'From homes to businesses, we provide professional exterior cleaning tailored to your property.',
  },
  {
    id: 'local',
    emoji: '📍',
    title: 'Local & Family-Owned',
    description:
      "We\'re a locally owned New Mexico business that takes pride in serving our community with honest pricing and quality workmanship.",
  },
];

export default function RGPSServiceArea() {
  function scrollToEstimate() {
    const el = document.getElementById('estimate') ?? document.querySelector('form');
    if (el) {
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.scrollBy({ top: 600, behavior: 'smooth' });
    }
  }

  return (
    <section id="service-area" className="bg-white py-20 px-4 sm:px-6">
      <div className="max-w-5xl mx-auto">

        {/* Section Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-teal-light text-teal font-bold text-sm px-4 py-2 rounded-full mb-4">
            📍 Service Areas
          </div>
          <h2
            className="text-dark-text font-bold mb-4"
            style={{ fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            Proudly Serving Central New Mexico
          </h2>
          <p className="text-gray-text text-lg max-w-3xl mx-auto leading-relaxed">
            Rio Grande Property Solutions proudly provides professional exterior cleaning services for residential and commercial properties throughout Central New Mexico. We&apos;re committed to reliable service, fast scheduling, and exceptional results wherever you need us.
          </p>
        </div>

        {/* Service Area List */}
        <div className="mb-14">
          <h3
            className="text-center text-dark-text font-bold text-xl mb-8"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            Areas We Serve
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl mx-auto">
            {serviceAreas?.map((area) => (
              <div
                key={area?.id}
                className={`group flex items-center gap-3 bg-white border rounded-2xl px-5 py-4 shadow-sm transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 cursor-default ${
                  area?.isPrimary
                    ? 'border-teal ring-1 ring-teal/20'
                    : area?.isSurrounding
                    ? 'border-dashed border-teal/40 bg-teal-50/30' :'border-gray-100 hover:border-teal/30'
                }`}
              >
                {/* Checkmark icon */}
                <span
                  className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${
                    area?.isSurrounding ? 'bg-teal/10' : 'bg-teal-light'
                  }`}
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#0ABFBC"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </span>

                {/* Label */}
                <span
                  className={`font-semibold text-sm leading-tight ${
                    area?.isSurrounding ? 'text-gray-500 italic' : 'text-dark-text'
                  }`}
                >
                  {area?.label}
                </span>

                {/* Primary badge */}
                {area?.isPrimary && (
                  <span className="ml-auto flex-shrink-0 bg-teal text-white text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap">
                    Primary Service Area
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Feature Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-14">
          {featureCards?.map((card) => (
            <div
              key={card?.id}
              className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-200 text-center"
            >
              <div className="text-4xl mb-4">{card?.emoji}</div>
              <h4
                className="text-dark-text font-bold text-base mb-2"
                style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              >
                {card?.title}
              </h4>
              <p className="text-gray-text text-sm leading-relaxed">{card?.description}</p>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-br from-teal-50 to-white border border-teal/20 rounded-3xl p-8 sm:p-10 text-center shadow-sm">
          <h3
            className="text-dark-text font-bold text-xl sm:text-2xl mb-3"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            Don&apos;t See Your City?
          </h3>
          <p className="text-gray-text text-base max-w-xl mx-auto mb-8 leading-relaxed">
            If you&apos;re near one of our service areas, there&apos;s a good chance we can still help. Contact us today to check availability.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://riograndepropertysolutions.booksy.com/a/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-teal hover:bg-teal-dark text-white font-bold px-8 py-4 rounded-full shadow-teal transition-all hover:scale-105 text-base"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              Check Availability
            </a>
            <button
              onClick={scrollToEstimate}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white hover:bg-teal-light text-teal border-2 border-teal font-bold px-8 py-4 rounded-full transition-all hover:scale-105 text-base"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <polyline points="10 9 9 9 8 9" />
              </svg>
              Get Free Estimate
            </button>
          </div>
        </div>

      </div>
    </section>
  );
}
